import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { motion, useScroll, useTransform, useMotionValueEvent, AnimatePresence } from 'framer-motion';
import { sections, hiddenLayer } from '../data/mock';
import AnimatedGridPattern from '../components/ui/animated-grid-pattern';
import VerticalCutReveal from '../components/ui/vertical-cut-reveal';
import { CursorFollower, ScrollProgress } from '../components/portfolio/CursorAndProgress';

const TOTAL_HEIGHT = 850; // vh

/* ─── Text style map ─── */
const getTextStyle = (style) => {
  const map = {
    'large':        'text-3xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight font-[Syne]',
    'large-hold':   'text-3xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight font-[Syne]',
    'medium':       'text-xl md:text-3xl lg:text-4xl font-medium tracking-tight',
    'medium-snap':  'text-2xl md:text-4xl lg:text-5xl font-semibold tracking-tight font-[Syne]',
    'stagger':      'text-xl md:text-3xl lg:text-4xl font-light tracking-wide',
    'tiny-impact':  'text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter font-[Syne]',
    'scatter':      'text-lg md:text-2xl lg:text-3xl font-light tracking-wide',
    'slow':         'text-2xl md:text-3xl lg:text-4xl font-light leading-relaxed',
    'center-scale': 'text-5xl md:text-7xl lg:text-8xl font-extrabold tracking-tighter font-[Syne]',
    'exit':         'text-xl md:text-2xl lg:text-3xl font-light tracking-wide opacity-70',
  };
  return map[style] || map['medium'];
};

const getAnimation = (style) => {
  const base = { initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -20 } };
  const map = {
    'large':        { ...base, transition: { duration: 0.9, ease: [0.25, 0.46, 0.45, 0.94] } },
    'large-hold':   { ...base, transition: { duration: 1.1, ease: [0.25, 0.46, 0.45, 0.94] } },
    'medium-snap':  { initial: { opacity: 0, x: -40 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0 }, transition: { duration: 0.35, ease: 'easeOut' } },
    'medium':       { ...base, transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] } },
    'stagger':      { ...base, transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] } },
    'tiny-impact':  { initial: { opacity: 0, scale: 0.7 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0, scale: 0.9 }, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
    'scatter':      { initial: { opacity: 0, filter: 'blur(8px)' }, animate: { opacity: 1, filter: 'blur(0px)' }, exit: { opacity: 0, filter: 'blur(4px)' }, transition: { duration: 0.7 } },
    'center-scale': { initial: { opacity: 0, scale: 0.5 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0, scale: 1.1 }, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
    'slow':         { ...base, transition: { duration: 1.1, ease: [0.25, 0.46, 0.45, 0.94] } },
    'exit':         { initial: { opacity: 0 }, animate: { opacity: 0.7 }, exit: { opacity: 0 }, transition: { duration: 1 } },
  };
  return map[style] || map['large'];
};

const DRAMATIC_STYLES = new Set(['large-hold', 'center-scale', 'tiny-impact', 'exit']);
const USE_REVEAL = new Set(['large', 'large-hold', 'medium-snap', 'tiny-impact', 'center-scale']);

/* ─── Typewriter hook ─── */
const useTypewriter = (lines, speed = 50) => {
  // lines: [{text, pause_after}]
  // Returns: array of displayed strings, one per line, plus done flag
  const [displayed, setDisplayed] = useState(lines.map(() => ''));
  const [done, setDone] = useState(false);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setStarted(true), 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!started) return;
    let lineIdx = 0;
    let charIdx = 0;
    let pausing = false;
    let pauseTimer = null;

    const tick = () => {
      if (lineIdx >= lines.length) { setDone(true); return; }
      const currentLine = lines[lineIdx].text;

      if (pausing) return;

      if (charIdx <= currentLine.length) {
        setDisplayed(prev => {
          const next = [...prev];
          next[lineIdx] = currentLine.slice(0, charIdx);
          return next;
        });
        charIdx++;

        if (charIdx > currentLine.length) {
          const pauseAfter = lines[lineIdx].pauseAfter ?? 600;
          pausing = true;
          pauseTimer = setTimeout(() => {
            pausing = false;
            lineIdx++;
            charIdx = 0;
            interval = setInterval(tick, speed);
          }, pauseAfter);
          clearInterval(interval);
          return;
        }
      }
    };

    let interval = setInterval(tick, speed);
    return () => {
      clearInterval(interval);
      if (pauseTimer) clearTimeout(pauseTimer);
    };
  }, [started]); // eslint-disable-line

  return { displayed, done };
};

/* ─── Typewriter Hero ─── */
const TypewriterHero = ({ onScrollPast }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const opacity = useTransform(scrollYProgress, [0, 0.55], [1, 0]);
  const y = useTransform(scrollYProgress, [0, 0.55], [0, -70]);

  useEffect(() => {
    const unsub = scrollYProgress.on('change', (v) => {
      onScrollPast(v > 0.45);
    });
    return unsub;
  }, [scrollYProgress, onScrollPast]);

  const lines = [
    { text: "Hi, I'm Rohan V Shetty.", pauseAfter: 700 },
    { text: "I don't build dashboards.", pauseAfter: 450 },
    { text: "I build decisions.", pauseAfter: 9999 },
  ];
  const { displayed, done } = useTypewriter(lines, 48);

  return (
    <motion.section ref={ref} className="h-[130vh] relative">
      <div className="sticky top-0 h-screen flex flex-col items-center justify-center z-10">
        <motion.div className="text-center px-8 max-w-3xl" style={{ opacity, y }}>

          {/* Line 1 — Name */}
          <div className="mb-3 min-h-[1.2em]">
            <span className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight font-[Syne]">
              {displayed[0]}
              {displayed[0].length < lines[0].text.length && (
                <span className="inline-block w-[3px] h-[0.85em] bg-black ml-1 align-middle animate-[blink_0.7s_infinite]" />
              )}
            </span>
          </div>

          {/* MBA subtitle — fades in once name finishes typing */}
          <AnimatePresence>
            {displayed[0].length >= lines[0].text.length && (
              <motion.p
                className="text-xs md:text-sm tracking-[0.25em] uppercase font-light opacity-40 mb-8"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 0.4, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                MBA — Business Analytics
              </motion.p>
            )}
          </AnimatePresence>

          {/* Lines 2 + 3 — only appear once line 1 is done */}
          <AnimatePresence>
            {displayed[1].length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="space-y-2"
              >
                <div className="min-h-[1.2em]">
                  <span className="text-xl md:text-3xl lg:text-4xl font-light tracking-wide opacity-70">
                    {displayed[1]}
                    {displayed[1].length < lines[1].text.length && (
                      <span className="inline-block w-[2px] h-[0.8em] bg-black ml-1 align-middle opacity-70 animate-[blink_0.7s_infinite]" />
                    )}
                  </span>
                </div>

                {displayed[2].length > 0 && (
                  <div className="min-h-[1.2em]">
                    <span className="text-xl md:text-3xl lg:text-4xl font-semibold tracking-wide font-[Syne]">
                      {displayed[2]}
                      {displayed[2].length < lines[2].text.length && (
                        <span className="inline-block w-[2px] h-[0.8em] bg-black ml-1 align-middle animate-[blink_0.7s_infinite]" />
                      )}
                    </span>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Scroll hint — appears after typewriter finishes */}
          <AnimatePresence>
            {done && (
              <motion.div
                className="mt-20 flex flex-col items-center gap-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.35 }}
                transition={{ delay: 0.8, duration: 1 }}
              >
                <motion.div
                  className="flex flex-col items-center gap-3"
                  animate={{ y: [0, 8, 0] }}
                  transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <p className="text-[10px] tracking-[0.35em] uppercase">Scroll</p>
                  <div className="w-[1px] h-8 bg-black opacity-40 overflow-hidden">
                    <motion.div
                      className="w-full bg-black"
                      animate={{ height: ['0%', '100%', '0%'] }}
                      transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
                    />
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.section>
  );
};

/* ─── Section progress dots ─── */
const SectionDots = ({ activeIndex, total }) => (
  <div className="fixed right-6 top-1/2 -translate-y-1/2 z-[9000] flex flex-col gap-3 pointer-events-none">
    {Array.from({ length: total }).map((_, i) => (
      <motion.div
        key={i}
        className="rounded-full bg-black"
        animate={{
          width: i === activeIndex ? 8 : 5,
          height: i === activeIndex ? 8 : 5,
          opacity: i === activeIndex ? 0.85 : 0.3,
        }}
        transition={{ duration: 0.3 }}
      />
    ))}
  </div>
);

/* ─── Animated text line ─── */
const TextLine = ({ text, style, delay, visible }) => {
  const [show, setShow] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (visible) {
      timerRef.current = setTimeout(() => setShow(true), delay);
    } else {
      setShow(false);
      if (timerRef.current) clearTimeout(timerRef.current);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [visible, delay]);

  const anim = getAnimation(style);
  const textClass = getTextStyle(style);
  const useReveal = USE_REVEAL.has(style);
  const isDramatic = DRAMATIC_STYLES.has(style);

  return (
    <AnimatePresence mode="wait">
      {show && (
        <motion.div
          initial={useReveal ? { opacity: 0 } : anim.initial}
          animate={useReveal ? { opacity: 1 } : anim.animate}
          exit={anim.exit}
          transition={useReveal ? { duration: 0.25 } : anim.transition}
          whileHover={{ x: 3, scale: 1.01, transition: { duration: 0.3 } }}
        >
          {useReveal ? (
            <VerticalCutReveal
              splitBy={isDramatic ? 'characters' : 'words'}
              staggerDuration={isDramatic ? 0.03 : 0.045}
              transition={{ type: 'spring', damping: isDramatic ? 22 : 30, stiffness: isDramatic ? 150 : 200 }}
              containerClassName={textClass}
            >
              {text}
            </VerticalCutReveal>
          ) : (
            <p className={textClass}>{text}</p>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ─── Scatter section ─── */
const ScatterSection = ({ lines, visible }) => (
  <div className="absolute inset-0">
    {lines.map((line, i) => (
      <div key={i} className={`scatter-${line.position}`}>
        <TextLine text={line.text} style={line.style} delay={line.delay} visible={visible} />
      </div>
    ))}
  </div>
);

/* ─── Section renderer ─── */
const SectionRenderer = ({ section, visible }) => {
  const isScatter = section.id === 'fragments';
  const isCenter = section.id === 'control';
  if (isScatter) return <ScatterSection lines={section.lines} visible={visible} />;
  return (
    <div className={`flex flex-col gap-6 md:gap-8 ${isCenter ? 'items-center justify-center text-center' : 'justify-center'}`}>
      {section.lines.map((line, i) => (
        <TextLine key={`${section.id}-${i}`} text={line.text} style={line.style} delay={line.delay} visible={visible} />
      ))}
    </div>
  );
};

/* ─── Hidden Layer ─── */
const HiddenLayer = ({ visible }) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    if (!visible) { setPhase(0); return; }
    const t1 = setTimeout(() => setPhase(1), 400);
    const t2 = setTimeout(() => setPhase(2), 1400);
    const t3 = setTimeout(() => setPhase(3), 2200);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [visible]);

  const handleResume = () => {
    document.body.style.transition = 'opacity 0.5s ease';
    document.body.style.opacity = '0.25';
    setTimeout(() => {
      document.body.style.opacity = '1';
      window.open(hiddenLayer.resumeUrl, '_blank');
    }, 500);
  };

  const btnBase = "text-xs tracking-[0.28em] uppercase border border-black px-7 py-3 bg-transparent text-black hover:bg-black hover:text-white cursor-none no-underline text-center";
  const btnStyle = { transition: 'background-color 0.35s ease, color 0.35s ease' };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="flex flex-col items-center justify-center gap-10 text-center px-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
        >
          {/* Line 1 */}
          <AnimatePresence>
            {phase >= 1 && (
              <motion.p
                className="text-2xl md:text-3xl font-light tracking-wide opacity-70 font-[Syne]"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 0.7, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                {hiddenLayer.line1}
              </motion.p>
            )}
          </AnimatePresence>

          {/* Line 2 */}
          <AnimatePresence>
            {phase >= 2 && (
              <motion.p
                className="text-xl md:text-2xl font-light tracking-wide opacity-40"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 0.4, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                {hiddenLayer.line2}
              </motion.p>
            )}
          </AnimatePresence>

          {/* CTAs */}
          <AnimatePresence>
            {phase >= 3 && (
              <motion.div
                className="flex flex-col items-center gap-5"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
              >
                <div className="flex flex-col md:flex-row items-center gap-3">
                  <motion.button onClick={handleResume} className={btnBase} style={btnStyle} whileHover={{ scale: 1.02 }}>
                    {hiddenLayer.buttonText}
                  </motion.button>
                  <motion.a href={hiddenLayer.linkedinUrl} target="_blank" rel="noopener noreferrer" className={btnBase} style={btnStyle} whileHover={{ scale: 1.02 }}>
                    LinkedIn
                  </motion.a>
                  <motion.a href={`mailto:${hiddenLayer.email}`} className={btnBase} style={btnStyle} whileHover={{ scale: 1.02 }}>
                    Email
                  </motion.a>
                </div>
                {/* Contact line */}
                <motion.p
                  className="text-[11px] tracking-[0.2em] opacity-25 mt-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.25 }}
                  transition={{ delay: 0.5, duration: 1 }}
                >
                  {hiddenLayer.name} &nbsp;·&nbsp; {hiddenLayer.email}
                </motion.p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ─── Main Portfolio ─── */
const Portfolio = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const [progress, setProgress] = useState(0);
  const prevProgress = useRef(0);
  const [introPassed, setIntroPassed] = useState(false);

  useMotionValueEvent(scrollYProgress, 'change', (latest) => {
    prevProgress.current = latest;
    setProgress(latest);
  });

  const activeSection = useMemo(() => {
    for (let i = 0; i < sections.length; i++) {
      const [start, end] = sections[i].range;
      if (progress >= start && progress < end) return i;
    }
    if (progress >= 0.97) return 'hidden';
    return -1;
  }, [progress]);

  const stickyY = useTransform(scrollYProgress, [0, 1], [0, -30]);

  return (
    <div className="bg-white text-black min-h-screen selection:bg-black selection:text-white relative">
      <CursorFollower />
      <ScrollProgress />
      <AnimatedGridPattern
        numSquares={45}
        maxOpacity={0.1}
        duration={5}
        repeatDelay={0}
        className="fixed inset-0 z-0 skew-y-0 text-gray-900/20 stroke-gray-900/10"
        width={40}
        height={40}
      />

      {/* Section dots — only while in main experience */}
      {activeSection !== -1 && activeSection !== 'hidden' && (
        <SectionDots activeIndex={activeSection} total={sections.length} />
      )}

      {/* Typewriter Hero */}
      <TypewriterHero onScrollPast={setIntroPassed} />

      {/* Scroll-driven experience */}
      <div ref={containerRef} style={{ height: `${TOTAL_HEIGHT}vh` }} className="relative">
        <div className="sticky top-0 h-screen w-full flex items-center z-10">
          <motion.div
            className="w-full h-full px-8 md:px-16 lg:px-24 xl:px-32 flex items-center relative"
            style={{ y: stickyY }}
          >
            {sections.map((section, i) => (
              <div
                key={section.id}
                className={`absolute inset-0 px-8 md:px-16 lg:px-24 xl:px-32 flex items-center ${
                  section.id === 'control' ? 'justify-center' : ''
                }`}
                style={{
                  opacity: activeSection === i ? 1 : 0,
                  pointerEvents: activeSection === i ? 'auto' : 'none',
                  transition: 'opacity 0.5s ease',
                }}
              >
                <div className="max-w-4xl w-full">
                  <SectionRenderer section={section} visible={activeSection === i} />
                </div>
              </div>
            ))}

            {/* Hidden layer */}
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                opacity: activeSection === 'hidden' ? 1 : 0,
                pointerEvents: activeSection === 'hidden' ? 'auto' : 'none',
                transition: 'opacity 0.7s ease',
              }}
            >
              <HiddenLayer visible={activeSection === 'hidden'} />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Portfolio;
